import { useEffect, useState } from 'react';
import sessionExpiredImage from '@/assets/images/session_expired.png';
import {
  RateLimitError,
  TokenExpiredError,
  clearSession,
  isLoginChallenge,
  logout,
  isSetupChallenge,
  login,
  onUnauthorized,
  verifyTwoFactorLogin,
} from '@/services/auth';

export default function SessionExpiredModal() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<'password' | 'verify'>('password');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [pendingToken, setPendingToken] = useState('');
  const [code, setCode] = useState('');
  const [useBackup, setUseBackup] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => onUnauthorized(() => setOpen(true)), []);

  const finish = () => {
    setOpen(false);
    setStep('password');
    setIdentifier('');
    setPassword('');
    setPendingToken('');
    setCode('');
    setUseBackup(false);
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const res = await login(identifier, password);
      if (isLoginChallenge(res)) {
        setPendingToken(res.pending_token); // in memory only
        setStep('verify');
      } else if (isSetupChallenge(res)) {
        // 2FA was reset server-side — enrollment needs the full login screen.
        clearSession();
        window.location.href = '/login';
      } else {
        finish(); // full session (non-dashboard) — already persisted
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to sign in. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await verifyTwoFactorLogin(pendingToken, code.trim());
      finish();
    } catch (err) {
      if (err instanceof TokenExpiredError) {
        setStep('password');
        setCode('');
        setPendingToken('');
        setError('That timed out. Enter your password again.');
      } else if (err instanceof RateLimitError) {
        setError(`${err.message} Try again in ${err.retryAfter}s.`);
      } else {
        setError(err instanceof Error ? err.message : 'Invalid code. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-stone-900/50 p-4">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
        <img
          src={sessionExpiredImage}
          alt=""
          className="mx-auto mb-3 h-32 w-32 object-contain"
        />
        <h2 className="text-center text-lg font-bold text-stone-800">Session expired</h2>
        <p className="mt-1 text-center text-sm text-stone-500">
          {step === 'password'
            ? 'Your session has expired. Sign in again to continue — your place on this page is kept.'
            : useBackup
              ? 'Enter one of your backup codes to continue.'
              : 'Enter the 6-digit code from your authenticator app.'}
        </p>

        {error && (
          <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-600">
            {error}
          </div>
        )}

        {step === 'verify' && (
          <form className="mt-4 space-y-3" onSubmit={handleVerify}>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-stone-500 mb-1">
                {useBackup ? 'Backup code' : 'Authentication code'}
              </label>
              <input
                type="text"
                inputMode={useBackup ? 'text' : 'numeric'}
                autoComplete="one-time-code"
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                placeholder={useBackup ? 'xxxx-xxxx' : '123456'}
                className="w-full rounded-xl border border-orange-100 bg-white px-3 py-2 text-center text-sm tracking-[0.3em] text-stone-800 outline-none transition focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
              />
            </div>
            <button
              type="submit"
              disabled={submitting || !code.trim()}
              className="mt-1 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-orange-600 via-orange-500 to-amber-400 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-orange-400/30 transition hover:brightness-105 focus:outline-none focus:ring-2 focus:ring-orange-300 disabled:opacity-60"
            >
              {submitting ? 'Verifying…' : 'Verify'}
            </button>
            <button
              type="button"
              onClick={() => {
                setUseBackup((v) => !v);
                setCode('');
                setError('');
              }}
              className="w-full text-center text-xs font-semibold text-orange-500 transition hover:text-orange-600"
            >
              {useBackup ? 'Use authenticator code' : 'Use a backup code instead'}
            </button>
          </form>
        )}

        {step === 'password' && (
        <form className="mt-4 space-y-3" onSubmit={handleSubmit} autoComplete="on">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-stone-500 mb-1">
              Email or username
            </label>
            <input
              type="text"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              required
              autoComplete="username"
              placeholder="you@example.com or username"
              className="w-full rounded-xl border border-orange-100 bg-white px-3 py-2 text-sm text-stone-800 outline-none transition focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-stone-500 mb-1">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              placeholder="Enter password"
              className="w-full rounded-xl border border-orange-100 bg-white px-3 py-2 text-sm text-stone-800 outline-none transition focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="mt-1 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-orange-600 via-orange-500 to-amber-400 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-orange-400/30 transition hover:brightness-105 focus:outline-none focus:ring-2 focus:ring-orange-300 disabled:opacity-60"
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
        )}

        <button
          type="button"
          onClick={() => {
            void logout().finally(() => {
              window.location.href = '/login';
            });
          }}
          className="mt-3 w-full text-center text-xs font-medium text-stone-400 transition hover:text-stone-600"
        >
          Log out instead
        </button>
      </div>
    </div>
  );
}
